/** @jsxImportSource react */
//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useNavigate } from 'react-router-dom';
import devOpsLogo from '../../icons/loop_logo.png';
import teamIllustration from '../../icons/team_2.png';

interface NoTeamspaceViewProps {
  t: (key: string) => string;
  isAdmin?: boolean;
}

const NoTeamspaceView: React.FC<NoTeamspaceViewProps> = ({ t, isAdmin }) => {
  const navigate = useNavigate();

  return (
    <div
      className="relative h-full overflow-hidden"
      id="id-no-teamspace-container"
      data-testid="no-teamspace-container"
    >
      <div
        className="mx-auto max-w-[40rem] pt-8 text-center"
        id="id-no-teamspace-content"
        data-testid="no-teamspace-content"
      >
        {/* Centered Logo */}
        <div
          className="mb-8 flex justify-center"
          id="id-no-teamspace-logo-wrapper"
          data-testid="no-teamspace-logo-wrapper"
        >
          <img
            src={devOpsLogo}
            alt="DevOps Loop"
            className="h-auto w-full max-w-[300px] object-contain"
          />
        </div>

        {/* Welcome Statement */}
        <h1 className="mb-2 text-2xl font-medium text-foreground">
          {t('no_teamspace_heading')}
        </h1>
        <p className="mb-10 text-secondary-foreground">
          {t('message-no-teamspace')}
        </p>

        {/* Three Instruction Steps */}
        <div
          className="mb-10 space-y-6 text-left"
          id="id-no-teamspace-steps-container"
          data-testid="no-teamspace-steps-container"
        >
          <div
            className="flex items-start gap-4"
            id="id-no-teamspace-step-1"
            data-testid="no-teamspace-step-1"
          >
            <div
              className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-primary"
              id="id-no-teamspace-step-1-circle"
              data-testid="no-teamspace-step-1-circle"
            >
              <div
                className="flex size-[22px] items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground"
                id="id-no-teamspace-step-1-number"
                data-testid="no-teamspace-step-1-number"
              >
                1
              </div>
            </div>
            <div
              id="id-no-teamspace-step-1-content"
              data-testid="no-teamspace-step-1-content"
            >
              <h3 className="font-medium text-foreground">
                {t('create-teamspace')}
              </h3>
              <p className="text-sm text-secondary-foreground">
                {t('no-teamspace-step1-description')}
              </p>
            </div>
          </div>

          <div
            className="flex items-start gap-4"
            id="id-no-teamspace-step-2"
            data-testid="no-teamspace-step-2"
          >
            <div
              className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-primary"
              id="id-no-teamspace-step-2-circle"
              data-testid="no-teamspace-step-2-circle"
            >
              <div
                className="flex size-[22px] items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground"
                id="id-no-teamspace-step-2-number"
                data-testid="no-teamspace-step-2-number"
              >
                2
              </div>
            </div>
            <div
              id="id-no-teamspace-step-2-content"
              data-testid="no-teamspace-step-2-content"
            >
              <h3 className="font-medium text-foreground">
                {t('no-teamspace-step2-title')}
              </h3>
              <p className="text-sm text-secondary-foreground">
                {t('no-teamspace-step2-description')}
              </p>
            </div>
          </div>

          <div
            className="flex items-start gap-4"
            id="id-no-teamspace-step-3"
            data-testid="no-teamspace-step-3"
          >
            <div
              className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-primary"
              id="id-no-teamspace-step-3-circle"
              data-testid="no-teamspace-step-3-circle"
            >
              <div
                className="flex size-[22px] items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground"
                id="id-no-teamspace-step-3-number"
                data-testid="no-teamspace-step-3-number"
              >
                3
              </div>
            </div>
            <div
              id="id-no-teamspace-step-3-content"
              data-testid="no-teamspace-step-3-content"
            >
              <h3 className="font-medium text-foreground">
                {t('no-teamspace-step3-title')}
              </h3>
              <p className="text-sm text-secondary-foreground">
                {t('no-teamspace-step3-description')}
              </p>
            </div>
          </div>
        </div>

        {/* Create a TeamSpace Button */}
        {isAdmin && (
          <button
            onClick={() => navigate('/teamspace')}
            className="rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
            id="id-no-teamspace-create-teamspace-btn-secondary"
            data-testid="id-no-teamspace-create-teamspace-btn-secondary"
          >
            {t('create-teamspace')}
          </button>
        )}
      </div>

      {/* Illustration - Bottom Right */}
      <div
        className="pointer-events-none absolute bottom-0 right-0 w-1/3 max-w-md"
        id="id-no-teamspace-illustration-wrapper"
        data-testid="no-teamspace-illustration-wrapper"
      >
        <img
          src={teamIllustration}
          alt="Team collaboration"
          className="h-auto w-full"
        />
      </div>
    </div>
  );
};

export default NoTeamspaceView;
